/**
 * Pendo event vocabulary for AQ-Cloud2.
 *
 * This file is the **complete, enumerable list** of custom actions the app
 * reports to Pendo. Components import a named helper from here; nothing should
 * call `trackEvent` (let alone `window.pendo`) directly. Keeping the vocabulary
 * in one module is what makes it possible to audit what leaves the app, to keep
 * event names stable for Pendo dashboards, and to review the privacy contract
 * below in a single diff.
 *
 * ## Naming
 * `<domain>_<action>`, snake_case, past tense for things that happened.
 * Domains: `catalog_`, `pdp_`, `cart_`, `project_`, `line_item_`, `pricing_`,
 * `quote_`, `report_`, `inventory_`, `contacts_`, `file_`, `onboarding_`.
 * These match AQ-Webcatalog's conventions (`pdp_viewed`, `catalog_search`) so
 * cross-app funnels line up in one Pendo subscription.
 *
 * ## PRIVACY CONTRACT — read before adding an event
 * Pendo runs with `excludeAllText: true`, so the agent captures no DOM text.
 * Event metadata is the one channel we control, and it must stay clean:
 *
 *   ALLOWED    opaque ids (productId, projectId, vendorId), enum-ish strings
 *              (status, tab name, format), counts, booleans, bucketed
 *              magnitudes, elapsed-time buckets.
 *   FORBIDDEN  customer names, company names, project names/titles, contact
 *              names, emails, addresses, phone numbers, free-text notes, and
 *              **raw money amounts** (unit price, margin %, totals, discounts).
 *
 * Money and other sensitive magnitudes go through `bucket()` — Pendo answers
 * "do users edit big quotes more often?", never "what did ACME pay?".
 * Search queries are the deliberate exception: `catalog_search` sends the
 * query text because zero-result analysis is worthless without it, and a
 * catalog search term is a product name, not customer data.
 */

import { trackEvent } from '@/lib/pendo'

// ---------------------------------------------------------------------------
// Metadata helpers
// ---------------------------------------------------------------------------

/**
 * Collapse a magnitude into an order-of-magnitude bucket label.
 *
 * Used for money and any other value that is commercially sensitive in the
 * raw. Buckets keep the analytical signal ("large quotes get edited more")
 * while making the underlying figure unrecoverable.
 */
export function bucket(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'unknown'
  const n = Math.abs(value)
  if (n === 0) return '0'
  if (n < 100) return '<100'
  if (n < 1_000) return '100-1k'
  if (n < 10_000) return '1k-10k'
  if (n < 100_000) return '10k-100k'
  if (n < 1_000_000) return '100k-1m'
  return '>1m'
}

/**
 * Collapse an elapsed duration (ms) into a latency bucket.
 */
export function latencyBucket(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return 'unknown'
  if (ms < 250) return '<250ms'
  if (ms < 1_000) return '250ms-1s'
  if (ms < 3_000) return '1s-3s'
  if (ms < 10_000) return '3s-10s'
  return '>10s'
}

/**
 * Collapse a byte size into a bucket label.
 */
export function sizeBucket(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return 'unknown'
  if (bytes < 100 * 1024) return '<100kb'
  if (bytes < 1024 * 1024) return '100kb-1mb'
  if (bytes < 10 * 1024 * 1024) return '1mb-10mb'
  if (bytes < 50 * 1024 * 1024) return '10mb-50mb'
  return '>50mb'
}

// ---------------------------------------------------------------------------
// Domain vocabularies
// ---------------------------------------------------------------------------

export type CatalogViewMode = 'rich' | 'compact' | 'preview'
export type TypeaheadKind = 'model' | 'brand' | 'category' | 'vendor' | 'product'
export type CatalogTileSource = 'popular' | 'favorites' | 'az' | 'browse-all'
export type ProjectCreateSource = 'blank' | 'cart' | 'duplicate' | 'clone'
export type LineItemSource = 'quick-add' | 'picker' | 'manual' | 'configured' | 'cart'
export type PricingField = 'cost' | 'margin' | 'discount' | 'net' | 'list' | 'multiplier'
export type PdpFileKind = 'cutsheet' | 'cad' | 'revit' | 'doc' | 'image' | 'other'
export type ExportFormat = 'pdf' | 'xlsx' | 'csv' | 'docx'
export type InventoryProductKind = 'custom' | 'from-catalog'
export type PricingChildKind = 'qty' | 'case' | 'deviated' | 'custom' | 'surcharge'
export type CrudAction = 'add' | 'update' | 'delete'

/**
 * The full set of AQ-Cloud2 Pendo events.
 *
 * Grouped to mirror the approved instrumentation plan. Every helper is a thin
 * wrapper over `trackEvent` — no branching, no side effects, safe to call from
 * a render-adjacent callback.
 */
export const PendoEvents = {
  // =========================================================================
  // Catalog discovery
  // =========================================================================

  /** A catalog search executed and returned. */
  catalogSearch: (params: {
    query: string
    resultCount: number
    elapsedMs?: number | undefined
    activeFacetCount?: number | undefined
    viewMode?: CatalogViewMode | undefined
  }): void =>
    trackEvent('catalog_search', {
      query: params.query,
      resultCount: params.resultCount,
      latency: latencyBucket(params.elapsedMs),
      activeFacetCount: params.activeFacetCount ?? 0,
      viewMode: params.viewMode ?? 'rich',
    }),

  /** A search returned nothing — the highest-value catalog-gap signal. */
  catalogZeroResults: (params: {
    query: string
    activeFacetCount?: number | undefined
    facetKeys?: string[] | undefined
  }): void =>
    trackEvent('catalog_zero_results', {
      query: params.query,
      activeFacetCount: params.activeFacetCount ?? 0,
      facetKeys: params.facetKeys?.join(',') ?? '',
    }),

  /** A typeahead suggestion was chosen. */
  catalogTypeaheadSelect: (params: {
    kind: TypeaheadKind
    position: number
    queryLength: number
  }): void =>
    trackEvent('catalog_typeahead_select', {
      kind: params.kind,
      position: params.position,
      queryLength: params.queryLength,
    }),

  /** A facet value was added or removed. */
  catalogFacetChange: (params: {
    facetType: string
    action: 'add' | 'remove'
    activeFacetCount: number
  }): void =>
    trackEvent('catalog_facet_change', {
      facetType: params.facetType,
      action: params.action,
      activeFacetCount: params.activeFacetCount,
    }),

  /** A result card/row was clicked through to the PDP (→ CTR). */
  catalogResultClick: (params: {
    productId: string
    position: number
    viewMode: CatalogViewMode
    query?: string | undefined
  }): void =>
    trackEvent('catalog_result_click', {
      productId: params.productId,
      position: params.position,
      viewMode: params.viewMode,
      hasQuery: Boolean(params.query),
    }),

  /** Results layout switched. */
  catalogViewModeChange: (params: { from: CatalogViewMode; to: CatalogViewMode }): void =>
    trackEvent('catalog_view_mode_change', { from: params.from, to: params.to }),

  /** A manufacturer tile on the catalog home was clicked. */
  catalogHomeTileClick: (params: {
    manufacturerId: string
    source: CatalogTileSource
    position?: number | undefined
  }): void =>
    trackEvent('catalog_home_tile_click', {
      manufacturerId: params.manufacturerId,
      source: params.source,
      position: params.position ?? -1,
    }),

  /** Manufacturer favourited/unfavourited. */
  mfrFavoriteToggled: (params: { manufacturerId: string; favorited: boolean }): void =>
    trackEvent('mfr_favorite_toggled', {
      manufacturerId: params.manufacturerId,
      favorited: params.favorited,
    }),

  /** Product favourited/unfavourited. */
  productFavoriteToggled: (params: { productId: string; favorited: boolean }): void =>
    trackEvent('product_favorite_toggled', {
      productId: params.productId,
      favorited: params.favorited,
    }),

  // =========================================================================
  // Product detail page
  // =========================================================================

  pdpViewed: (params: {
    productId: string
    vendorId?: string | undefined
    hasConfig?: boolean | undefined
    hasPricing?: boolean | undefined
  }): void =>
    trackEvent('pdp_viewed', {
      productId: params.productId,
      vendorId: params.vendorId ?? '',
      hasConfig: Boolean(params.hasConfig),
      hasPricing: Boolean(params.hasPricing),
    }),

  pdpTabSwitch: (params: { productId: string; from: string; to: string }): void =>
    trackEvent('pdp_tab_switch', {
      productId: params.productId,
      from: params.from,
      to: params.to,
    }),

  pdpGalleryInteraction: (params: {
    productId: string
    interaction: 'zoom' | 'navigate' | 'thumbnail'
    imageIndex?: number | undefined
  }): void =>
    trackEvent('pdp_gallery_interaction', {
      productId: params.productId,
      interaction: params.interaction,
      imageIndex: params.imageIndex ?? -1,
    }),

  pdpFileDownload: (params: { productId: string; fileKind: PdpFileKind; mediaId?: string | undefined }): void =>
    trackEvent('pdp_file_download', {
      productId: params.productId,
      fileKind: params.fileKind,
      mediaId: params.mediaId ?? '',
    }),

  pdpAccessoryClick: (params: { productId: string; accessoryId: string }): void =>
    trackEvent('pdp_accessory_click', {
      productId: params.productId,
      accessoryId: params.accessoryId,
    }),

  pdpAccessoryAdded: (params: { productId: string; accessoryId: string }): void =>
    trackEvent('pdp_accessory_added', {
      productId: params.productId,
      accessoryId: params.accessoryId,
    }),

  pdpAddToCart: (params: { productId: string; qty: number; hasConfig?: boolean | undefined }): void =>
    trackEvent('pdp_add_to_cart', {
      productId: params.productId,
      qty: params.qty,
      hasConfig: Boolean(params.hasConfig),
    }),

  /** Catalog product pulled into the tenant's own inventory. */
  stockFromCatalog: (params: { productId: string; source: 'pdp' | 'results' | 'inventory' }): void =>
    trackEvent('stock_from_catalog', {
      productId: params.productId,
      source: params.source,
    }),

  // =========================================================================
  // Cart → project conversion (core funnel)
  // =========================================================================

  cartItemAdded: (params: { productId: string; qty: number; cartSize: number }): void =>
    trackEvent('cart_item_added', {
      productId: params.productId,
      qty: params.qty,
      cartSize: params.cartSize,
    }),

  cartItemRemoved: (params: { productId: string; cartSize: number }): void =>
    trackEvent('cart_item_removed', {
      productId: params.productId,
      cartSize: params.cartSize,
    }),

  cartCreateProject: (params: { cartSize: number; lineItemCount: number }): void =>
    trackEvent('cart_create_project', {
      cartSize: params.cartSize,
      lineItemCount: params.lineItemCount,
    }),

  cartAddToExistingProject: (params: {
    projectId: string
    cartSize: number
    lineItemCount: number
  }): void =>
    trackEvent('cart_add_to_existing_project', {
      projectId: params.projectId,
      cartSize: params.cartSize,
      lineItemCount: params.lineItemCount,
    }),

  // =========================================================================
  // Projects
  // =========================================================================

  projectCreated: (params: { projectId: string; source: ProjectCreateSource; market?: string | undefined }): void =>
    trackEvent('project_created', {
      projectId: params.projectId,
      source: params.source,
      market: params.market ?? '',
    }),

  projectOpened: (params: {
    projectId: string
    status?: string | undefined
    lineItemCount?: number | undefined
    tab?: string | undefined
  }): void =>
    trackEvent('project_opened', {
      projectId: params.projectId,
      status: params.status ?? '',
      lineItemCount: params.lineItemCount ?? -1,
      tab: params.tab ?? '',
    }),

  /** Reveals which of the ~12 project tabs are actually used. */
  projectTabSwitch: (params: { projectId: string; from: string; to: string }): void =>
    trackEvent('project_tab_switch', {
      projectId: params.projectId,
      from: params.from,
      to: params.to,
    }),

  /** Field *names* only — never the values (customer/project names). */
  projectHeaderUpdated: (params: { projectId: string; fields: string[] }): void =>
    trackEvent('project_header_updated', {
      projectId: params.projectId,
      fields: params.fields.join(','),
      fieldCount: params.fields.length,
    }),

  projectStatusChanged: (params: { projectId: string; from: string; to: string }): void =>
    trackEvent('project_status_changed', {
      projectId: params.projectId,
      from: params.from,
      to: params.to,
    }),

  projectLockAction: (params: {
    projectId: string
    action: 'lock' | 'unlock'
  }): void =>
    trackEvent('project_lock_action', {
      projectId: params.projectId,
      action: params.action,
    }),

  projectLifecycleAction: (params: {
    projectId: string
    action: 'duplicate' | 'archive' | 'unarchive' | 'recycle' | 'restore' | 'owner-change'
  }): void =>
    trackEvent('project_lifecycle_action', {
      projectId: params.projectId,
      action: params.action,
    }),

  projectBulkAction: (params: { action: string; count: number }): void =>
    trackEvent('project_bulk_action', { action: params.action, count: params.count }),

  projectSavedView: (params: { action: CrudAction | 'apply'; target: string }): void =>
    trackEvent('project_saved_view', { action: params.action, target: params.target }),

  projectColumnsChanged: (params: { visibleCount: number; totalCount: number }): void =>
    trackEvent('project_columns_changed', {
      visibleCount: params.visibleCount,
      totalCount: params.totalCount,
    }),

  projectDiffViewed: (params: {
    projectId: string
    fromVersion?: string | undefined
    toVersion?: string | undefined
  }): void =>
    trackEvent('project_diff_viewed', {
      projectId: params.projectId,
      hasVersionRange: Boolean(params.fromVersion && params.toVersion),
    }),

  // =========================================================================
  // Line items (quote grid)
  // =========================================================================

  lineItemAdded: (params: {
    projectId: string
    source: LineItemSource
    productId?: string | undefined
    count?: number | undefined
  }): void =>
    trackEvent('line_item_added', {
      projectId: params.projectId,
      source: params.source,
      productId: params.productId ?? '',
      count: params.count ?? 1,
    }),

  lineItemConfigured: (params: {
    projectId: string
    productId: string
    optionCount: number
  }): void =>
    trackEvent('line_item_configured', {
      projectId: params.projectId,
      productId: params.productId,
      optionCount: params.optionCount,
    }),

  /**
   * A pricing cell was edited. Sends the *field* and a bucketed magnitude —
   * never the entered amount (see the privacy contract).
   */
  lineItemPricingEdited: (params: {
    projectId: string
    field: PricingField
    hadReasonCode?: boolean | undefined
    amount?: number | undefined
  }): void =>
    trackEvent('line_item_pricing_edited', {
      projectId: params.projectId,
      field: params.field,
      hadReasonCode: Boolean(params.hadReasonCode),
      amountBucket: bucket(params.amount),
    }),

  lineItemBulkAction: (params: { projectId: string; action: string; count: number }): void =>
    trackEvent('line_item_bulk_action', {
      projectId: params.projectId,
      action: params.action,
      count: params.count,
    }),

  /** Batch grid save — how much work users queue before committing. */
  lineItemDraftsSaved: (params: { projectId: string; draftCount: number }): void =>
    trackEvent('line_item_drafts_saved', {
      projectId: params.projectId,
      draftCount: params.draftCount,
    }),

  lineItemDeleted: (params: { projectId: string; count: number }): void =>
    trackEvent('line_item_deleted', { projectId: params.projectId, count: params.count }),

  lineItemsReordered: (params: { projectId: string; count: number }): void =>
    trackEvent('line_items_reordered', { projectId: params.projectId, count: params.count }),

  lineItemReaccessorized: (params: { projectId: string; productId: string }): void =>
    trackEvent('line_item_reaccessorized', {
      projectId: params.projectId,
      productId: params.productId,
    }),

  subLineItemAction: (params: { projectId: string; action: CrudAction }): void =>
    trackEvent('sub_line_item_action', { projectId: params.projectId, action: params.action }),

  lineItemEditorOpened: (params: { projectId: string; tab?: string | undefined }): void =>
    trackEvent('line_item_editor_opened', {
      projectId: params.projectId,
      tab: params.tab ?? 'default',
    }),

  netPriceReasonSubmitted: (params: { projectId: string; reason: string }): void =>
    trackEvent('net_price_reason_submitted', {
      projectId: params.projectId,
      reason: params.reason,
    }),

  // =========================================================================
  // Pricing & totals
  // =========================================================================

  /** Margin guardrail fired — the clearest signal of pricing-policy friction. */
  marginGuardrailTriggered: (params: {
    projectId: string
    level: 'warn' | 'block'
    context: 'line-item' | 'totals'
  }): void =>
    trackEvent('margin_guardrail_triggered', {
      projectId: params.projectId,
      level: params.level,
      context: params.context,
    }),

  totalsRecomputed: (params: { projectId: string; trigger: 'manual' | 'auto' }): void =>
    trackEvent('totals_recomputed', { projectId: params.projectId, trigger: params.trigger }),

  additionalTotalChanged: (params: {
    projectId: string
    action: CrudAction
    kind?: string | undefined
  }): void =>
    trackEvent('additional_total_changed', {
      projectId: params.projectId,
      action: params.action,
      kind: params.kind ?? '',
    }),

  freightItemChanged: (params: { projectId: string; action: CrudAction }): void =>
    trackEvent('freight_item_changed', { projectId: params.projectId, action: params.action }),

  freightEstimateRequested: (params: { projectId: string; provider: string }): void =>
    trackEvent('freight_estimate_requested', {
      projectId: params.projectId,
      provider: params.provider,
    }),

  freightEstimateApplied: (params: { projectId: string; provider: string; amount?: number | undefined }): void =>
    trackEvent('freight_estimate_applied', {
      projectId: params.projectId,
      provider: params.provider,
      amountBucket: bucket(params.amount),
    }),

  pricingVisibilityToggled: (params: { visible: boolean; surface: string }): void =>
    trackEvent('pricing_visibility_toggled', {
      visible: params.visible,
      surface: params.surface,
    }),

  // =========================================================================
  // Quote lifecycle & approvals
  // =========================================================================

  quoteTermAction: (params: {
    projectId: string
    action: 'request' | 'approve' | 'deny' | 'cancel'
    termType?: string | undefined
  }): void =>
    trackEvent('quote_term_action', {
      projectId: params.projectId,
      action: params.action,
      termType: params.termType ?? '',
    }),

  projectVersionAction: (params: { projectId: string; action: 'snapshot' | 'restore' }): void =>
    trackEvent('project_version_action', {
      projectId: params.projectId,
      action: params.action,
    }),

  moveToOrder: (params: { projectId: string; phase: 'started' | 'completed' }): void =>
    trackEvent('move_to_order', { projectId: params.projectId, phase: params.phase }),

  purchaseOrderAction: (params: {
    projectId: string
    action: CrudAction
    poId?: string | undefined
  }): void =>
    trackEvent('purchase_order_action', {
      projectId: params.projectId,
      action: params.action,
      poId: params.poId ?? '',
    }),

  poLifecycleTransition: (params: { projectId: string; from: string; to: string }): void =>
    trackEvent('po_lifecycle_transition', {
      projectId: params.projectId,
      from: params.from,
      to: params.to,
    }),

  // =========================================================================
  // Proposals, reports & exports
  // =========================================================================

  proposalGenerated: (params: {
    projectId: string
    reportType: string
    optionCount?: number | undefined
  }): void =>
    trackEvent('proposal_generated', {
      projectId: params.projectId,
      reportType: params.reportType,
      optionCount: params.optionCount ?? 0,
    }),

  /** Recipient *count* only — never the addresses. */
  proposalSent: (params: {
    projectId: string
    channel: 'email' | 'link' | 'download'
    recipientCount: number
  }): void =>
    trackEvent('proposal_sent', {
      projectId: params.projectId,
      channel: params.channel,
      recipientCount: params.recipientCount,
    }),

  reportStudioOpened: (params: { projectId: string; reportType?: string | undefined }): void =>
    trackEvent('report_studio_opened', {
      projectId: params.projectId,
      reportType: params.reportType ?? '',
    }),

  reportPreviewed: (params: { projectId: string; reportType: string }): void =>
    trackEvent('report_previewed', {
      projectId: params.projectId,
      reportType: params.reportType,
    }),

  reportExported: (params: {
    projectId: string
    reportType: string
    format: ExportFormat
  }): void =>
    trackEvent('report_exported', {
      projectId: params.projectId,
      reportType: params.reportType,
      format: params.format,
    }),

  projectShareAction: (params: {
    projectId: string
    action: 'create' | 'revoke'
    permission?: string | undefined
  }): void =>
    trackEvent('project_share_action', {
      projectId: params.projectId,
      action: params.action,
      permission: params.permission ?? '',
    }),

  // =========================================================================
  // Inventory / product authoring
  // =========================================================================

  inventoryProductCreated: (params: { productId?: string | undefined; kind: InventoryProductKind }): void =>
    trackEvent('inventory_product_created', {
      productId: params.productId ?? '',
      kind: params.kind,
    }),

  /** Panel names reveal which editor sections matter; no field values. */
  inventoryProductUpdated: (params: { productId: string; panels: string[] }): void =>
    trackEvent('inventory_product_updated', {
      productId: params.productId,
      panels: params.panels.join(','),
      panelCount: params.panels.length,
    }),

  inventoryProductDeleted: (params: { productId: string }): void =>
    trackEvent('inventory_product_deleted', { productId: params.productId }),

  inventoryImportUploaded: (params: { rowCount?: number | undefined; fileType?: string | undefined }): void =>
    trackEvent('inventory_import_uploaded', {
      rowCount: params.rowCount ?? -1,
      fileType: params.fileType ?? '',
    }),

  inventoryImportApplied: (params: {
    inserted: number
    updated: number
    failed: number
  }): void =>
    trackEvent('inventory_import_applied', {
      inserted: params.inserted,
      updated: params.updated,
      failed: params.failed,
    }),

  inventorySyncTriggered: (params: { sourceId?: string | undefined }): void =>
    trackEvent('inventory_sync_triggered', { sourceId: params.sourceId ?? '' }),

  inventoryApiKeyAction: (params: { action: 'create' | 'revoke' }): void =>
    trackEvent('inventory_api_key_action', { action: params.action }),

  inventorySettingsUpdated: (params: { fields: string[] }): void =>
    trackEvent('inventory_settings_updated', {
      fields: params.fields.join(','),
      fieldCount: params.fields.length,
    }),

  productPricingChildChanged: (params: {
    productId: string
    kind: PricingChildKind
    action: CrudAction
  }): void =>
    trackEvent('product_pricing_child_changed', {
      productId: params.productId,
      kind: params.kind,
      action: params.action,
    }),

  productMediaUploaded: (params: { productId: string; count: number; kind?: string | undefined }): void =>
    trackEvent('product_media_uploaded', {
      productId: params.productId,
      count: params.count,
      kind: params.kind ?? '',
    }),

  privateCatalogPublished: (params: { productCount?: number | undefined }): void =>
    trackEvent('private_catalog_published', { productCount: params.productCount ?? -1 }),

  // =========================================================================
  // Contacts / CRM
  // =========================================================================

  /** Ids only — company and person names are customer data. */
  contactsEntityChanged: (params: {
    entity: 'company' | 'person' | 'address' | 'opportunity' | 'customer-number'
    action: CrudAction
    entityId?: string | undefined
  }): void =>
    trackEvent('contacts_entity_changed', {
      entity: params.entity,
      action: params.action,
      entityId: params.entityId ?? '',
    }),

  contactsImportStarted: (params: { profileId?: string | undefined; rowCount?: number | undefined }): void =>
    trackEvent('contacts_import_started', {
      hasProfile: Boolean(params.profileId),
      rowCount: params.rowCount ?? -1,
    }),

  contactsImportCompleted: (params: {
    inserted: number
    updated: number
    failed: number
  }): void =>
    trackEvent('contacts_import_completed', {
      inserted: params.inserted,
      updated: params.updated,
      failed: params.failed,
    }),

  contactsDedupeMerged: (params: { mergedCount: number }): void =>
    trackEvent('contacts_dedupe_merged', { mergedCount: params.mergedCount }),

  // =========================================================================
  // Files & attachments
  // =========================================================================

  fileUploaded: (params: { context: string; bytes?: number | undefined; mimeType?: string | undefined }): void =>
    trackEvent('file_uploaded', {
      context: params.context,
      size: sizeBucket(params.bytes),
      mimeType: params.mimeType ?? '',
    }),

  projectAttachmentAction: (params: {
    projectId: string
    action: 'add' | 'preview' | 'delete' | 'download'
  }): void =>
    trackEvent('project_attachment_action', {
      projectId: params.projectId,
      action: params.action,
    }),

  // =========================================================================
  // Activation funnel
  // =========================================================================

  registrationStep: (params: { step: 'started' | 'verified' | 'completed' }): void =>
    trackEvent('registration_step', { step: params.step }),

  onboardingStepCompleted: (params: { step: string; index: number; totalSteps: number }): void =>
    trackEvent('onboarding_step_completed', {
      step: params.step,
      index: params.index,
      totalSteps: params.totalSteps,
    }),

  onboardingCompleted: (params: { stepsCompleted: number }): void =>
    trackEvent('onboarding_completed', { stepsCompleted: params.stepsCompleted }),
} as const

export type PendoEventName = keyof typeof PendoEvents
